import { environment } from './../../../environments/environment';

import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Seo, JsonLd } from 'src/_models/seo';

import { readCurrentLocaleOnce } from '../../_shared/locale/current-url';

// Brand suffix dolaczany do <title>/og:title — lokalizowany przez i18n
// (klucz seo.brandSuffix), z polskim fallbackiem dla zgodnosci wstecznej.
const BRAND_SUFFIX_FALLBACK = 'Sushi Poznań Drzewo Wiśni';

// Mapowanie locale aplikacji -> og:locale (OpenGraph oczekuje formatu xx_XX).
const OG_LOCALE: Record<string, string> = {
  pl: 'pl_PL',
  en: 'en_GB',
};

// Jezyki obslugiwane przez hreflang. Od T13 warianty maja forme SCIEZKOWA
// (`/menu` i `/en/menu`), a nie `?lang=` — patrz localePageUrl() nizej.
const HREFLANG_LOCALES = ['pl', 'en'] as const;

/**
 * Prefiks ADRESU dla locale. 🔴 H6: polski to PUSTY STRING, ZAWSZE.
 * Zaden canonical, og:url, hreflang ani `<loc>` w sitemapie nie zawiera `/pl/`.
 * To jedyny scenariusz, w ktorym ta zmiana moze zaszkodzic POLSKIM pozycjom
 * w Google — stad niezmiennik jako test, nie jako komentarz.
 */
export const LOCALE_URL_PREFIX: Record<'pl' | 'en', string> = {
  pl: '',
  en: '/en',
};

/**
 * Percent-encoding sciezki, idempotentny.
 *
 * Po co: `/polityka-prywatności` musi trafiac do canonicala, og:url i hreflang
 * jako `/polityka-prywatno%C5%9Bci`. Kodujemy SEGMENT PO SEGMENCIE, zeby nie
 * zakodowac ukosnikow, i pomijamy segmenty, ktore JUZ sa zakodowane — inaczej
 * drugie przejscie dalo by `%25C5%259Bci` (podwojne kodowanie), a wartosc
 * wchodzi tu raz ze `data.seo`, a raz juz z adresu.
 */
export function encodePathSegments(path: string): string {
  return path
    .split('/')
    .map((segment) => {
      if (!segment) return segment;
      // Juz zakodowany? (dekodowanie zmienia tresc => wejscie bylo zakodowane)
      try {
        if (decodeURIComponent(segment) !== segment) return segment;
      } catch {
        return segment;
      }
      return encodeURIComponent(segment);
    })
    .join('/');
}

// Atrybut-marker pozwalajacy zidentyfikowac (i wyczyscic przy SPA-nav) tylko
// te <link rel="alternate">, ktore wstrzyknal SeoService.
const HREFLANG_MARKER = 'data-seo-hreflang';

/**
 * Skleja baseUrl ze sciezka z data.seo w BEZPIECZNY sposob.
 *
 * Bez tego guardu kazda sciezka bez wiodacego ukosnika (np. canonical: '404')
 * konkatenowala sie w 'https://drzewowisni.pl404' — adres spoza domeny, ktory
 * trafial do canonical, og:url i hreflang. Normalizacja dzieje sie tutaj, w
 * serwisie, wiec zla dana w routingu nie jest juz w stanie wyprodukowac
 * niepoprawnego URL-a.
 *
 * Zasady:
 *  - pusto / same biale znaki  -> korzen '/'
 *  - URL absolutny (http(s)://) -> zwracany bez zmian
 *  - brak wiodacego '/'    -> dopisywany
 *  - zwielokrotnione '/' na poczatku -> zwijane do jednego; '//menu' traktujemy
 *    jako literowke w sciezce, a NIE jako URL protocol-relative — canonical
 *    wskazujacy na obcy host bylby grozniejszy niz poprawiona sciezka
 */
export function buildAbsoluteUrl(baseUrl: string, path?: string): string {
  const base = (baseUrl ?? '').replace(/\/+$/, '');
  const raw = (path ?? '').trim();

  if (!raw) {
    return `${base}/`;
  }
  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }
  return base + '/' + raw.replace(/^\/+/, '');
}

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  defaultSeo: Seo = {
    title: 'Sushi Poznań - restauracja japońska, sushi bar, sushi na wynos',
    titleKey: 'seo.home.title',
    canonical: '',
    description:
      '→ Klasyczyna japońska restauracja ✓ Zarezerwuj online lub zadzwoń ✓ Sprawdź menu !',
    descriptionKey: 'seo.home.description',
  };
  /**
   * Locale BIEZACEGO ADRESU, ustalane DOKLADNIE RAZ, w konstruktorze.
   *
   * 🔴 Zrodlem jest ADRES (wariant B-full: prefiks niesie go na obu platformach),
   * NIGDY `translate.currentLang`. W przegladarce `currentLang` zalezy jeszcze od
   * localStorage i po hydracji potrafi sie rozjechac z tym, co poszlo w HTML-u
   * z SSR — a wtedy Googlebot dostaje INNY canonical niz czlowiek w devtoolsach
   * (§4.1: canonical weryfikujemy `curl`-em na surowym HTML-u, nigdy inspektorem).
   *
   * Dlaczego w konstruktorze, a nie w updateSeo(): `readCurrentLocaleOnce()` czyta
   * DI (PlatformLocation / REQUEST) i MUSI byc wolane w kontekscie wstrzykiwania.
   * Raz na dokument wystarcza — zmiana jezyka to zmiana ADRESU i pelny reload
   * (T8), wiec w zyciu jednego dokumentu prefiks sie nie zmienia.
   */
  private readonly locale: 'pl' | 'en' = readCurrentLocaleOnce();

  constructor(
    @Inject(DOCUMENT) private dom: Document,
    private titleService: Title,
    private metaTagService: Meta,
    private translate: TranslateService
  ) {}

  // Zwraca przetlumaczona wartosc dla klucza i18n; gdy klucz pusty albo
  // tlumaczenie nie istnieje (instant zwraca sam klucz), uzywa literala.
  private resolve(key: string | undefined, literal: string): string {
    if (!key) {
      return literal;
    }
    const value = this.translate.instant(key);
    return value && value !== key ? value : literal;
  }

  private get brandSuffix(): string {
    return this.resolve('seo.brandSuffix', BRAND_SUFFIX_FALLBACK);
  }

  /**
   * Absolutny URL dla sciezki z data.seo — BEZ prefiksu locale.
   * Uzywane dla `og:image` i wszystkiego, co nie jest adresem STRONY.
   */
  private absolute(path?: string): string {
    return buildAbsoluteUrl(environment.baseUrl, path);
  }

  /**
   * Absolutny adres STRONY w podanym locale (T13 — canonical, og:url, hreflang).
   *
   * `environment.baseUrl + prefiks + seo.canonical`, gdzie prefiks to '' | '/en'.
   *
   * To jest cala naprawa, po ktora istnieje ten projekt: dzis `/menu` i `/en/menu`
   * emituja IDENTYCZNY canonical `base + /menu`, czyli strona angielska
   * DEINDEKSUJE SIE WLASNYM TAGIEM. Po tej zmianie kazdy wariant jezykowy
   * wskazuje na siebie.
   *
   * 🔴 Pulapka korzenia: dla `canonical: ''` sklejka `base + '/en' + ''` dala by
   * `base/en` — a kontrakt §1 wiersz 3 wymaga `base/en/` (a `/en` bez ukosnika
   * to 302 na `/en/`). Canonical wskazujacy na adres przekierowujacy to ten sam
   * blad klasy „canonical klamie", ktory faza 0 naprawiala.
   */
  private localePageUrl(canonical: string | undefined, locale: 'pl' | 'en'): string {
    const prefix = LOCALE_URL_PREFIX[locale];
    const raw = (canonical ?? '').trim();
    if (/^https?:\/\//i.test(raw)) {
      // Absolutny URL w data.seo — nie prefiksujemy obcego hosta.
      return raw;
    }
    const path = encodePathSegments(raw);
    if (!path || path === '/') {
      // Korzen: '/' dla PL, '/en/' dla EN.
      return buildAbsoluteUrl(environment.baseUrl, prefix ? `${prefix}/` : '/');
    }
    return buildAbsoluteUrl(environment.baseUrl, `${prefix}/${path.replace(/^\/+/, '')}`);
  }

  /** Adres TEJ strony w locale wziętym z adresu (canonical / og:url). */
  private selfUrl(canonical?: string): string {
    return this.localePageUrl(canonical, this.locale);
  }

  private get ogLocale(): string {
    return OG_LOCALE[this.translate.currentLang] ?? OG_LOCALE['pl'];
  }

  updateSeo(seo?: Seo): void {
    seo ??= this.defaultSeo;
    const title = this.resolve(seo.titleKey, seo.title);
    const description = this.resolve(seo.descriptionKey, seo.description);
    const head = this.dom.getElementsByTagName('head')[0];
    let element: HTMLLinkElement | null =
      this.dom.querySelector(`link[rel='canonical']`) || null;
    if (element == null) {
      element = this.dom.createElement('link');
      head.appendChild(element);
    }
    element.setAttribute('rel', 'canonical');
    element.setAttribute('href', this.selfUrl(seo.canonical));
    this.titleService.setTitle(title + ' | ' + this.brandSuffix);
    this.metaTagService.updateTag({
      name: 'description',
      content: description,
    });
    this.updateOGTags(seo, title, description);
    this.updateHreflang(seo);
    this.updateRobots(seo);
    this.setJsonLd(seo.jsonLd);
  }

  // Generuje <link rel="alternate" hreflang="pl|en|x-default"> dla biezacej
  // strony. T13: wariant per-locale to SCIEZKA ('/menu' i '/en/menu'), nie
  // '?lang='; x-default = adres bezprefiksowy (kanoniczny PL). Ustawiane juz
  // w initial SSR (operacja na DOM przez DOCUMENT, spojnie z canonical).
  // Mechanika zostaje bez zmian — jest poprawna: przy kazdej nawigacji SPA
  // czysci poprzednie linki po markerze i wstawia komplet od nowa.
  private updateHreflang(seo: Seo): void {
    const head = this.dom.getElementsByTagName('head')[0];
    if (!head) {
      return;
    }
    // Wyczysc poprzedni komplet (tylko nasze, po markerze) — chroni przed
    // duplikacja przy nawigacji client-side w SPA.
    this.dom
      .querySelectorAll(`link[${HREFLANG_MARKER}]`)
      .forEach((node) => node.parentNode?.removeChild(node));

    // T13: forma SCIEZKOWA, nie `?lang=`. Google konsoliduje sygnaly na adresach
    // z sitemapy i canonicali — a te sa od teraz sciezkowe. Zestaw `?lang=`,
    // ktory Google zna z dzisiejszych hreflangow, normalizator Express (T12)
    // przekierowuje 302 na te wlasnie adresy, wiec to konsolidacja, nie strata.
    for (const locale of HREFLANG_LOCALES) {
      this.appendAlternate(head, locale, this.localePageUrl(seo.canonical, locale));
    }
    // x-default -> adres bezprefiksowy (kanoniczny polski). 🔴 H6: '' , nie '/pl'.
    this.appendAlternate(head, 'x-default', this.localePageUrl(seo.canonical, 'pl'));
  }

  private appendAlternate(head: HTMLElement, hreflang: string, href: string): void {
    const link = this.dom.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', hreflang);
    link.setAttribute('href', href);
    link.setAttribute(HREFLANG_MARKER, '');
    head.appendChild(link);
  }

  // <meta name="robots">: ustawiane tylko dla stron z seo.noindex, a przy kazdej
  // innej nawigacji USUWANE — inaczej noindex z /404 "przyklejalby sie" do
  // kolejnych stron w tej samej sesji SPA (tag zyje w <head> miedzy nawigacjami).
  private updateRobots(seo: Seo): void {
    if (seo.noindex) {
      this.metaTagService.updateTag({
        name: 'robots',
        content: 'noindex, follow',
      });
    } else {
      this.metaTagService.removeTag(`name='robots'`);
    }
  }

  private setJsonLd(jsonLd?: JsonLd): void {
    let ldJsonScriptTag = this.dom.head.querySelector(
      `script[type='application/ld+json']`
    );
    if (ldJsonScriptTag) {
      ldJsonScriptTag.textContent = JSON.stringify(jsonLd);
    } else {
      ldJsonScriptTag = this.dom.createElement('script');
      ldJsonScriptTag.setAttribute('type', 'application/ld+json');
      ldJsonScriptTag.textContent = JSON.stringify(jsonLd);
      this.dom.head.appendChild(ldJsonScriptTag);
    }
  }

  updateOGTags(seo: Seo, title: string, description: string): void {
    this.metaTagService.updateTag({
      property: 'og:site_name',
      content: 'Drzewo Wiśni',
    });
    this.metaTagService.updateTag({
      property: 'og:locale',
      content: this.ogLocale,
    });
    this.metaTagService.updateTag({
      property: 'og:url',
      content: this.selfUrl(seo.canonical),
    });
    this.metaTagService.updateTag({
      property: 'og:type',
      content: seo.type || 'website',
    });
    this.metaTagService.updateTag({
      property: 'og:title',
      content: title + ' | ' + this.brandSuffix,
    });
    this.metaTagService.updateTag({
      property: 'og:description',
      content: description,
    });
    this.metaTagService.updateTag({
      property: 'og:image',
      content: seo.image ? this.absolute(seo.image) : '',
    });
  }
}
